import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/authStore';

export default function VerifyEmailPage() {
  const [code, setCode] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const {
    isLoading,
    error,
    verificationRequired,
    verificationEmail,
    confirmEmailVerification,
    resendVerificationCode,
    clearError,
  } = useAuthStore();
  const navigate = useNavigate();

  // Clear any stale errors from other auth pages
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Redirect if no verification in progress
  if (!verificationRequired || !verificationEmail) {
    return <Navigate to="/register" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setResendSuccess(false);
    try {
      await confirmEmailVerification(verificationEmail, code);
      navigate('/email-login', { state: { emailVerified: true } });
    } catch (_error) {
      // Error handled by store
    }
  };

  const handleResend = async () => {
    clearError();
    setResendSuccess(false);
    try {
      await resendVerificationCode(verificationEmail);
      setResendSuccess(true);
    } catch (_error) {
      // Error handled by store
    }
  };

  return (
    <main className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2">Verify your email</h1>
        <p className="text-gray-400 mb-6">
          Enter the 6-digit code sent to{' '}
          <span className="text-white font-medium">{verificationEmail}</span>
        </p>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Resend Success */}
        {resendSuccess && (
          <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-4 mb-6">
            <p className="text-green-400">A new code has been sent to your email.</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="verification-code"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              Verification code
            </label>
            <input
              id="verification-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-center text-2xl tracking-widest placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="000000"
              required
              autoComplete="one-time-code"
              autoFocus
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isLoading ? 'Verifying...' : 'Verify email'}
          </button>
        </form>

        {/* Resend Link */}
        <p className="text-center mt-6 text-gray-400">
          Didn&apos;t receive a code?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={isLoading}
            className="text-purple-400 hover:text-purple-300 disabled:opacity-50"
          >
            Resend code
          </button>
        </p>
      </div>
    </main>
  );
}
